import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Wallet, CreditCard, Link2, Users, ChevronRight, Check, Loader2, Clock } from "lucide-react";
import { money } from "@/lib/api";

const GOOGLE = "https://www.svgrepo.com/show/475656/google-color.svg";

function AppleGlyph() {
  return (
    <svg viewBox="0 0 384 512" className="h-5 w-5 fill-white">
      <path d="M318.7 268c-.2-36.7 16.4-64.4 50-84.8-18.8-27-47.2-41.8-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C64.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-92.6zM255.4 90.6c30.6-36.3 27.8-69.4 26.9-81.3-26.6 1.5-57.4 18.1-74.9 38.5-19.3 21.9-30.6 49-28.2 78.3 28.8 2.2 55.1-12.6 76.2-35.5z"/>
    </svg>
  );
}

const Brand = ({ bg, children }) => (
  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
    {children}
  </div>
);

export default function PaymentMethodModal({ open, onClose, total = 0, walletBalance = 0, onConfirm }) {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const toComplete = Math.max(0, total - walletBalance);

  const methods = [
    {
      id: "wallet", title: `Portefeuille (${money(walletBalance)}) + complément`,
      sub: `Solde insuffisant · ${money(toComplete)} à compléter`,
      icon: <Brand bg="#3A3410"><span className="text-[#F5C518] font-bold text-lg">$</span></Brand>,
    },
    {
      id: "card", title: "Carte de crédit / débit", sub: "Visa, Mastercard, Amex",
      icon: <Brand bg="#1f2430"><CreditCard className="h-5 w-5 text-slate-200" /></Brand>,
    },
    {
      id: "apple", title: "Apple Pay", sub: "Paiement rapide Touch ID / Face ID",
      icon: <Brand bg="#111"><AppleGlyph /></Brand>,
    },
    {
      id: "google", title: "Google Pay", sub: "Paiement sécurisé via Google",
      icon: <Brand bg="#ffffff"><img src={GOOGLE} alt="" className="h-5 w-5" /></Brand>,
    },
    {
      id: "interac", title: "Virement Interac direct", sub: "Depuis votre compte bancaire",
      icon: <Brand bg="#FB6A2E"><span className="text-white font-bold text-[10px] leading-none text-center">INTERAC</span></Brand>,
    },
    {
      id: "sezzle", title: "Sezzle", sub: "Payez en 4 versements, sans intérêt",
      icon: <Brand bg="#7C4DFF"><span className="text-white font-bold text-[11px] lowercase">sezzle</span></Brand>,
    },
    {
      id: "link", title: "Envoyer un lien de paiement", sub: "Par courriel ou SMS · payé par un proche",
      icon: <Brand bg="#153043"><Link2 className="h-5 w-5 text-sky-300" /></Brand>,
    },
    {
      id: "coparent", title: "Co-parentalité express", sub: "L'autre parent paie un % du total", badge: "PARTAGE",
      icon: <Brand bg="#241537"><Users className="h-5 w-5 text-violet-300" /></Brand>,
    },
  ];

  const confirm = () => {
    if (!selected) return;
    setBusy(true);
    onConfirm(selected);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-modal" className="max-w-md p-0 border border-border bg-card rounded-2xl overflow-hidden gap-0">
        <DialogTitle className="sr-only">Sélectionner le mode de paiement</DialogTitle>
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">3</span>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Confirmer</span>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Sélectionner le mode de paiement</h2>
          <p className="text-sm text-muted-foreground mt-1">Choisissez un mode de paiement parmi les options ci-dessous.</p>
        </div>

        <div className="px-6 pb-2 max-h-[52vh] overflow-y-auto space-y-2.5">
          {methods.map((m) => (
            <button
              key={m.id}
              data-testid={`pay-option-${m.id}`}
              onClick={() => setSelected(m.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${selected === m.id ? "border-primary bg-foreground/5 ring-1 ring-primary" : "border-border hover:bg-foreground/5"}`}
            >
              {m.icon}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-semibold text-foreground text-sm truncate">{m.title}</p>
                  {m.badge && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-lime-400 text-black">{m.badge}</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{m.sub}</p>
              </div>
              {selected === m.id ? <Check className="h-5 w-5 text-primary shrink-0" /> : <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />}
            </button>
          ))}
        </div>

        {selected && (
          <div data-testid="pay-coming-soon" className="mx-6 mb-1 mt-1 flex items-start gap-2 text-sm text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5">
            <Clock className="h-4 w-4 shrink-0 mt-0.5" />
            <span><strong>À venir sous peu.</strong> Le paiement en ligne arrive prochainement. Votre commande sera confirmée dès maintenant — le règlement se fera à la cueillette ou à la livraison.</span>
          </div>
        )}

        <div className="p-5 border-t border-border">
          <p className="text-[11px] text-muted-foreground mb-3 text-center">Paiement de démonstration — le paiement en ligne réel (EcoPay) arrive bientôt.</p>
          <button
            onClick={confirm}
            disabled={!selected || busy}
            data-testid="pay-confirm-button"
            className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40 font-heading font-semibold transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer la commande · {money(total)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
